/**
 * 📊 Dashboard Detail — vista de un dashboard con sus widgets
 * Permite agregar/editar widgets y ver datos en tiempo real.
 * Ruta: /apps/reports/dashboards/:dashboardId
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDatosCacheados } from '@/hooks/useDatosCacheados';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Modal,
  TextField,
  Select,
  Badge,
  Box,
  Spinner,
  InlineGrid,
  Banner,
  Divider,
  ProgressBar,
} from '@/lib/polaris';
import {
  ChartVerticalFilledIcon,
  PlusIcon,
  EditIcon,
  DeleteIcon,
  RefreshIcon,
} from '@/lib/polaris-icons';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { ROUTES } from '@/config/routes.config';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { useParams } from 'react-router-dom';

// ─── Types ──────────────────────────────────────────────────
interface DashboardWidget {
  id: string;
  title: string;
  widget_type: string;
  data_source: string;
  config: WidgetConfig;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
}

interface WidgetConfig {
  metric: string;
  metric_field?: string;
  group_by?: string;
  filters?: Array<{ field: string; op: string; value: unknown }>;
  date_range?: string;
  date_from?: string;
  date_to?: string;
  comparison?: string;
  limit?: number;
  sort_by?: string;
  sort_dir?: string;
  color?: string;
  columns?: string[];
}

interface WidgetData {
  mode: string;
  value?: number;
  data?: Array<{ label: string; value: number }>;
  total?: number;
  metric?: string;
}

interface Dashboard {
  id: string;
  name: string;
  description: string | null;
}

// ─── Constants ──────────────────────────────────────────────
const WIDGET_TYPE_OPTIONS = [
  { label: 'KPI (número grande)', value: 'kpi' },
  { label: 'Gráfico de barras', value: 'bar_chart' },
  { label: 'Gráfico de líneas', value: 'line_chart' },
  { label: 'Gráfico circular', value: 'pie_chart' },
  { label: 'Tabla de datos', value: 'table' },
  { label: 'Embudo', value: 'funnel' },
  { label: 'Barra de progreso', value: 'progress' },
  { label: 'Lista top', value: 'list' },
];

const DATA_SOURCE_OPTIONS = [
  { label: 'Órdenes', value: 'orders' },
  { label: 'Items de orden', value: 'order_items' },
  { label: 'Deals (CRM)', value: 'crm_deals' },
  { label: 'Leads (CRM)', value: 'crm_leads' },
  { label: 'Llamadas (CRM)', value: 'crm_calls' },
  { label: 'Actividades (CRM)', value: 'crm_activities' },
  { label: 'Emails', value: 'email_messages' },
  { label: 'Productos', value: 'products' },
  { label: 'Clientes', value: 'customers' },
  { label: 'Pagos', value: 'payments' },
  { label: 'Uso de descuentos', value: 'discount_redemptions' },
];

const METRIC_OPTIONS = [
  { label: 'Contar', value: 'count' },
  { label: 'Sumar', value: 'sum' },
  { label: 'Promedio', value: 'avg' },
  { label: 'Mínimo', value: 'min' },
  { label: 'Máximo', value: 'max' },
];

const DATE_RANGE_OPTIONS = [
  { label: 'Hoy', value: 'today' },
  { label: 'Últimos 7 días', value: '7d' },
  { label: 'Últimos 30 días', value: '30d' },
  { label: 'Últimos 90 días', value: '90d' },
  { label: 'Este año', value: 'ytd' },
  { label: 'Todo', value: 'all' },
];

const METRIC_FIELDS: Record<string, Array<{ label: string; value: string }>> = {
  orders: [
    { label: 'Total', value: 'total' },
    { label: 'Subtotal', value: 'subtotal' },
    { label: 'Costo envío', value: 'delivery_cost' },
    { label: 'Descuento', value: 'discount_amount' },
  ],
  order_items: [
    { label: 'Cantidad', value: 'quantity' },
    { label: 'Precio unitario', value: 'unit_price' },
    { label: 'Total línea', value: 'total_price' },
  ],
  crm_deals: [
    { label: 'Monto', value: 'amount' },
    { label: 'Probabilidad', value: 'probability' },
  ],
  crm_leads: [
    { label: 'Score', value: 'score' },
  ],
  crm_calls: [
    { label: 'Duración', value: 'duration' },
  ],
  email_messages: [
    { label: 'Aperturas', value: 'opened_count' },
    { label: 'Clicks', value: 'clicked_count' },
  ],
  products: [
    { label: 'Precio', value: 'price' },
    { label: 'Stock', value: 'stock_quantity' },
  ],
  customers: [
    { label: 'Total órdenes', value: 'total_orders' },
    { label: 'Total gastado', value: 'total_spent' },
  ],
  payments: [
    { label: 'Monto', value: 'amount' },
  ],
  discount_redemptions: [
    { label: 'Monto descuento', value: 'discount_amount' },
  ],
};

const GROUP_BY_FIELDS: Record<string, Array<{ label: string; value: string }>> = {
  orders: [
    { label: 'Estado', value: 'status' },
    { label: 'Estado pago', value: 'payment_status' },
    { label: 'Método pago', value: 'payment_method' },
    { label: 'Método envío', value: 'delivery_method' },
    { label: 'Por día', value: 'created_at:day' },
    { label: 'Por semana', value: 'created_at:week' },
    { label: 'Por mes', value: 'created_at:month' },
  ],
  crm_deals: [
    { label: 'Estado', value: 'status' },
    { label: 'Por mes', value: 'created_at:month' },
  ],
  crm_leads: [
    { label: 'Estado', value: 'status' },
    { label: 'Fuente', value: 'source' },
    { label: 'Por mes', value: 'created_at:month' },
  ],
  crm_calls: [
    { label: 'Dirección', value: 'direction' },
    { label: 'Estado', value: 'status' },
    { label: 'Por día', value: 'created_at:day' },
  ],
  email_messages: [
    { label: 'Estado', value: 'status' },
    { label: 'Dirección', value: 'direction' },
    { label: 'Por mes', value: 'created_at:month' },
  ],
  products: [
    { label: 'Categoría', value: 'category' },
    { label: 'Estado', value: 'status' },
  ],
  payments: [
    { label: 'Proveedor', value: 'provider_code' },
    { label: 'Estado', value: 'status' },
    { label: 'Por mes', value: 'created_at:month' },
  ],
};

// ─── Component ──────────────────────────────────────────────
export default function ReportDashboardDetailPage() {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const storeNavigate = useStoreNavigate();
  const { tenantId } = useAuth();
  const toast = useAdminToast();

  const [widgetData, setWidgetData] = useState<Record<string, WidgetData>>({});
  const [loadingWidgets, setLoadingWidgets] = useState(false);

  // Widget form
  const [widgetDialogOpen, setWidgetDialogOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState<DashboardWidget | null>(null);
  const [wTitle, setWTitle] = useState('');
  const [wType, setWType] = useState('kpi');
  const [wSource, setWSource] = useState('orders');
  const [wMetric, setWMetric] = useState('count');
  const [wMetricField, setWMetricField] = useState('');
  const [wGroupBy, setWGroupBy] = useState('');
  const [wDateRange, setWDateRange] = useState('30d');
  const [wSaving, setWSaving] = useState(false);

  const [deleteWidget, setDeleteWidget] = useState<DashboardWidget | null>(null);

  // ─── Load dashboard + widgets ─────────────────────────────
  // Por el camino pavimentado de la caché (ADR-0012, paso 3). El tablero y sus widgets viajan
  // juntos: un tablero sin sus widgets es una pantalla en blanco con título.
  const { datos: tablero, cargando: loading, recargar: loadDashboard } = useDatosCacheados<{ dashboard: Dashboard | null; widgets: DashboardWidget[] }>({
    clave: ['report-dashboard', tenantId, dashboardId],
    activo: !!tenantId && !!dashboardId,
    frescura: 60_000,
    mensajeDeError: 'Dashboard no encontrado',
    traer: async () => {
      const [dashRes, widgetsRes] = await Promise.all([
        supabase.from('dashboards').select('id, name, description').eq('id', dashboardId!).single(),
        supabase
          .from('dashboard_widgets')
          .select('*')
          .eq('dashboard_id', dashboardId!)
          .eq('tenant_id', tenantId!)
          .order('position_y')
          .order('position_x'),
      ]);
      if (dashRes.error) throw dashRes.error;
      return {
        dashboard: (dashRes.data as Dashboard) ?? null,
        widgets: (widgetsRes.data as unknown as DashboardWidget[]) ?? [],
      };
    },
  });

  const dashboard = tablero?.dashboard ?? null;
  const widgets = useMemo(() => tablero?.widgets ?? [], [tablero]);



  // ─── Fetch widget data from Edge Function ─────────────────
  const fetchWidgetData = useCallback(async (w: DashboardWidget): Promise<WidgetData | null> => {
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) return null;

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const res = await fetch(`${supabaseUrl}/functions/v1/report-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          data_source: w.data_source,
          metric: w.config.metric || 'count',
          metric_field: w.config.metric_field,
          group_by: w.config.group_by,
          date_range: w.config.date_range || '30d',
          filters: w.config.filters,
          limit: w.config.limit || 10,
          sort_by: w.config.sort_by,
          sort_dir: w.config.sort_dir,
          columns: w.config.columns,
        }),
      });

      return await res.json();
    } catch {
      return null;
    }
  }, []);

  const refreshAllWidgets = useCallback(async () => {
    if (widgets.length === 0) return;
    setLoadingWidgets(true);

    const results = await Promise.all(
      widgets.map(async (w) => {
        const data = await fetchWidgetData(w);
        return { id: w.id, data };
      }),
    );

    const newData: Record<string, WidgetData> = {};
    for (const r of results) {
      if (r.data) newData[r.id] = r.data;
    }
    setWidgetData(newData);
    setLoadingWidgets(false);
  }, [widgets, fetchWidgetData]);

  useEffect(() => {
    if (widgets.length > 0) refreshAllWidgets();
  }, [widgets.length]); // Only on initial load or widget count change

  // ─── Widget CRUD ──────────────────────────────────────────
  const resetWidgetForm = () => {
    setWTitle('');
    setWType('kpi');
    setWSource('orders');
    setWMetric('count');
    setWMetricField('');
    setWGroupBy('');
    setWDateRange('30d');
  };

  const openAddWidget = () => {
    setEditingWidget(null);
    resetWidgetForm();
    setWidgetDialogOpen(true);
  };

  const openEditWidget = (w: DashboardWidget) => {
    setEditingWidget(w);
    setWTitle(w.title);
    setWType(w.widget_type);
    setWSource(w.data_source);
    setWMetric(w.config.metric || 'count');
    setWMetricField(w.config.metric_field || '');
    setWGroupBy(w.config.group_by || '');
    setWDateRange(w.config.date_range || '30d');
    setWidgetDialogOpen(true);
  };

  const handleSaveWidget = async () => {
    if (!tenantId || !dashboardId || !wTitle.trim()) return;
    setWSaving(true);

    const config: WidgetConfig = {
      metric: wMetric,
      metric_field: wMetricField || undefined,
      group_by: wGroupBy || undefined,
      date_range: wDateRange,
      sort_by: 'value',
      sort_dir: 'desc',
      limit: 10,
    };

    const row = {
      tenant_id: tenantId,
      dashboard_id: dashboardId,
      title: wTitle.trim(),
      widget_type: wType,
      data_source: wSource,
      config,
      position_x: 0,
      position_y: widgets.length,
      width: wType === 'kpi' ? 3 : 6,
      height: wType === 'kpi' ? 2 : 4,
    };

    try {
      if (editingWidget) {
        const { error } = await supabase.from('dashboard_widgets').update(row).eq('id', editingWidget.id);
        if (error) throw error;
        toast.success('Widget actualizado');
      } else {
        const { error } = await supabase.from('dashboard_widgets').insert(row);
        if (error) throw error;
        toast.success('Widget agregado');
      }
      setWidgetDialogOpen(false);
      await loadDashboard();
    } catch (error: unknown) {
      toast.error('Error al guardar widget', getErrorMessage(error));
    } finally {
      setWSaving(false);
    }
  };

  const handleDeleteWidget = async () => {
    if (!deleteWidget) return;
    try {
      const { error } = await supabase.from('dashboard_widgets').delete().eq('id', deleteWidget.id);
      if (error) throw error;
      toast.success('Widget eliminado');
      setDeleteWidget(null);
      await loadDashboard();
    } catch (error: unknown) {
      toast.error('Error al eliminar', getErrorMessage(error));
    }
  };

  // ─── Widget Renderers ─────────────────────────────────────
  const renderWidgetContent = (w: DashboardWidget) => {
    const data = widgetData[w.id];

    if (loadingWidgets || !data) {
      return (
        <Box padding="400">
          <InlineStack align="center"><Spinner size="small" /></InlineStack>
        </Box>
      );
    }

    if (data.mode === 'kpi') {
      const value = data.value ?? 0;
      const formatted = typeof value === 'number' && w.config.metric_field
        ? value.toLocaleString('es-PE', { maximumFractionDigits: 2 })
        : value.toLocaleString('es-PE');
      return (
        <Box padding="400">
          <BlockStack gap="100">
            <Text as="span" variant="headingXl" alignment="center">{formatted}</Text>
            <Text as="span" tone="subdued" variant="bodySm" alignment="center">
              {METRIC_OPTIONS.find(m => m.value === w.config.metric)?.label ?? w.config.metric}
              {w.config.metric !== 'count' && w.config.metric_field ? ` de ${w.config.metric_field}` : ''}
            </Text>
          </BlockStack>
        </Box>
      );
    }

    if (data.mode === 'grouped' && data.data) {
      const maxVal = Math.max(...data.data.map(d => d.value), 1);

      if (w.widget_type === 'bar_chart' || w.widget_type === 'progress' || w.widget_type === 'funnel') {
        return (
          <Box padding="300">
            <BlockStack gap="200">
              {data.data.map((item, i) => (
                <BlockStack key={i} gap="050">
                  <InlineStack align="space-between">
                    <Text as="span" variant="bodySm">{item.label}</Text>
                    <Text as="span" variant="bodySm" fontWeight="semibold">
                      {item.value.toLocaleString('es-PE', { maximumFractionDigits: 2 })}
                    </Text>
                  </InlineStack>
                  <ProgressBar
                    progress={Math.round((item.value / maxVal) * 100)}
                    size="small"
                    tone={i === 0 ? 'primary' : 'highlight'}
                  />
                </BlockStack>
              ))}
            </BlockStack>
          </Box>
        );
      }

      if (w.widget_type === 'list' || w.widget_type === 'table') {
        return (
          <Box padding="300">
            <BlockStack gap="100">
              {data.data.map((item, i) => (
                <div key={i}>
                  <InlineStack align="space-between">
                    <InlineStack gap="200" blockAlign="center">
                      <Badge tone="info">{i + 1}</Badge>
                      <Text as="span" variant="bodySm">{item.label}</Text>
                    </InlineStack>
                    <Text as="span" variant="bodySm" fontWeight="semibold">
                      {item.value.toLocaleString('es-PE', { maximumFractionDigits: 2 })}
                    </Text>
                  </InlineStack>
                  {i < data.data!.length - 1 && <Divider />}
                </div>
              ))}
            </BlockStack>
          </Box>
        );
      }

      // pie_chart / donut_chart — show as labeled bars (sin librería de charts)
      return (
        <Box padding="300">
          <BlockStack gap="200">
            {data.data.map((item, i) => {
              const pct = Math.round((item.value / data.data!.reduce((s, d) => s + d.value, 0)) * 100);
              return (
                <InlineStack key={i} align="space-between" blockAlign="center">
                  <Text as="span" variant="bodySm">{item.label}</Text>
                  <InlineStack gap="200" blockAlign="center">
                    <Text as="span" variant="bodySm" fontWeight="semibold">
                      {item.value.toLocaleString('es-PE', { maximumFractionDigits: 2 })}
                    </Text>
                    <Badge>{pct}%</Badge>
                  </InlineStack>
                </InlineStack>
              );
            })}
          </BlockStack>
        </Box>
      );
    }

    return (
      <Box padding="400">
        <Text as="p" tone="subdued" alignment="center">Sin datos</Text>
      </Box>
    );
  };

  // ─── Render ───────────────────────────────────────────────
  if (loading) {
    return (
      <Page title="Cargando…">
        <Card><Box padding="800"><InlineStack align="center"><Spinner size="large" /></InlineStack></Box></Card>
      </Page>
    );
  }

  if (!dashboard) {
    return (
      <Page title="Dashboard no encontrado">
        <Banner tone="critical">
          <p>El dashboard no existe o no tienes acceso.</p>
        </Banner>
      </Page>
    );
  }

  return (
    <Page
      title={
        <PageBreadcrumbTitle
          icon={ChartVerticalFilledIcon}
          title={dashboard.name}
          onBack={() => storeNavigate(ROUTES.APPS_REPORTS_DASHBOARDS)}
          backAccessibilityLabel="Volver a dashboards"
        />
      }
      primaryAction={{ content: 'Agregar widget', icon: PlusIcon, onAction: openAddWidget }}
      secondaryActions={[
        { content: 'Actualizar datos', icon: RefreshIcon, onAction: refreshAllWidgets, loading: loadingWidgets },
      ]}
    >
      <BlockStack gap="400">
        {widgets.length === 0 ? (
          <Card>
            <Box padding="800">
              <BlockStack gap="200" inlineAlign="center">
                <Text as="p" tone="subdued">Este dashboard está vacío.</Text>
                <Button onClick={openAddWidget}>Agregar primer widget</Button>
              </BlockStack>
            </Box>
          </Card>
        ) : (
          <InlineGrid columns={{ xs: 1, sm: 2, lg: 4 }} gap="400">
            {widgets.map((w) => (
              <Card key={w.id} padding="0">
                <BlockStack gap="0">
                  <Box padding="300" background="bg-surface-secondary">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="h3" variant="headingSm">{w.title}</Text>
                      <InlineStack gap="100">
                        <Badge>{DATE_RANGE_OPTIONS.find(d => d.value === w.config.date_range)?.label ?? w.config.date_range}</Badge>
                        <Button icon={EditIcon} variant="plain" size="slim" onClick={() => openEditWidget(w)} accessibilityLabel="Editar" />
                        <Button icon={DeleteIcon} variant="plain" size="slim" tone="critical" onClick={() => setDeleteWidget(w)} accessibilityLabel="Eliminar" />
                      </InlineStack>
                    </InlineStack>
                  </Box>
                  {renderWidgetContent(w)}
                </BlockStack>
              </Card>
            ))}
          </InlineGrid>
        )}
      </BlockStack>

      {/* Modal Agregar/Editar Widget */}
      <Modal
        open={widgetDialogOpen}
        onClose={() => setWidgetDialogOpen(false)}
        title={editingWidget ? 'Editar widget' : 'Nuevo widget'}
        primaryAction={{ content: 'Guardar', onAction: handleSaveWidget, loading: wSaving, disabled: !wTitle.trim() }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setWidgetDialogOpen(false) }]}
        large
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField label="Título" value={wTitle} onChange={setWTitle} placeholder="Ej: Ventas del mes" autoComplete="off" requiredIndicator />
            <InlineGrid columns={2} gap="400">
              <Select label="Tipo de widget" options={WIDGET_TYPE_OPTIONS} value={wType} onChange={setWType} />
              <Select label="Fuente de datos" options={DATA_SOURCE_OPTIONS} value={wSource} onChange={(v) => { setWSource(v); setWMetricField(''); setWGroupBy(''); }} />
            </InlineGrid>
            <InlineGrid columns={2} gap="400">
              <Select label="Métrica" options={METRIC_OPTIONS} value={wMetric} onChange={setWMetric} />
              {wMetric !== 'count' && METRIC_FIELDS[wSource] && (
                <Select
                  label="Campo"
                  options={[{ label: 'Seleccionar…', value: '' }, ...(METRIC_FIELDS[wSource] ?? [])]}
                  value={wMetricField}
                  onChange={setWMetricField}
                />
              )}
            </InlineGrid>
            {GROUP_BY_FIELDS[wSource] && (
              <Select
                label="Agrupar por"
                options={[{ label: 'Sin agrupación', value: '' }, ...(GROUP_BY_FIELDS[wSource] ?? [])]}
                value={wGroupBy}
                onChange={setWGroupBy}
                helpText="Agrupa los datos para crear gráficos de barras, líneas, etc."
              />
            )}
            <Select label="Rango de fechas" options={DATE_RANGE_OPTIONS} value={wDateRange} onChange={setWDateRange} />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Modal eliminar widget */}
      <Modal
        open={!!deleteWidget}
        onClose={() => setDeleteWidget(null)}
        title="Eliminar widget"
        primaryAction={{ content: 'Eliminar', onAction: handleDeleteWidget, destructive: true }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setDeleteWidget(null) }]}
      >
        <Modal.Section>
          <Text as="p">¿Eliminar el widget <strong>{deleteWidget?.title}</strong>?</Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
